import { useState } from 'react';
import type { z } from 'zod';
import { toast } from 'sonner';
import { ApiError } from './api';

/**
 * Small form helper: validates with the same zod schema the API uses (instant, friendly errors),
 * then maps any server-side field errors back onto the form.
 */
export function useForm<S extends z.ZodType<object>>(schema: S, initial: z.input<S>) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const set = <K extends keyof z.input<S>>(key: K, value: z.input<S>[K]) => {
    setValues((v) => ({ ...(v as object), [key]: value }) as z.input<S>);
    if (errors[key as string]) setErrors(({ [key as string]: _, ...rest }) => rest);
  };

  const bind = (key: keyof z.input<S> & string) => ({
    value: (values[key] ?? '') as string,
    onChange: (e: { target: { value: string } }) => set(key, e.target.value as z.input<S>[typeof key]),
    error: errors[key],
    name: key,
  });

  async function submit(action: (data: z.output<S>) => Promise<void>) {
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[issue.path.join('.')] ??= issue.message;
      setErrors(next);
      return;
    }
    setSubmitting(true);
    try {
      await action(parsed.data);
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length) setErrors(err.fields);
      else toast.error(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return { values, setValues, set, bind, errors, setErrors, submitting, submit };
}
